import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Inbox, MessageCircle, Receipt, ClipboardCheck, DollarSign } from "lucide-react";

const KIND_META = {
  parent_reply:      { icon: MessageCircle,  path: "/messages",       label: "رد ولي أمر"  },
  new_message:       { icon: MessageCircle,  path: "/messages",       label: "رسالة جديدة" },
  evaluation_frozen: { icon: ClipboardCheck, path: "/evaluations",    label: "تقييم رسمي جديد" },
  invoice_issued:    { icon: Receipt,        path: "/invoices",       label: "فاتورة جديدة" },
  invoice_paid:      { icon: DollarSign,     path: "/invoices",       label: "تأكيد دفع" },
};

export default function InboxBell() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (alive) setItems(data || []);
      } catch (_) { /* ignore */ }
    };
    tick();
    const iv = setInterval(tick, 20000);
    const onClick = (e) => {
      if (!e.target.closest?.("[data-inbox-root]")) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => { alive = false; clearInterval(iv); document.removeEventListener("mousedown", onClick); };
  }, [user]);

  const unread = items.filter((x) => !x.read);
  const count = unread.length;

  const goTo = async (n) => {
    const meta = KIND_META[n.kind];
    setOpen(false);
    if (meta?.path) navigate(meta.path);
    // mark all message-related read via child endpoint if applicable
    try {
      if (n.child_id && (n.kind === "parent_reply" || n.kind === "new_message")) {
        await api.post(`/messages/mark-read?child_id=${n.child_id}`);
      }
    } catch (_) { /* ignore */ }
    // Refresh
    try { const { data } = await api.get("/notifications"); setItems(data || []); } catch (_) { /* ignore */ }
  };

  return (
    <div className="relative" data-inbox-root>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="inbox-bell"
        aria-label="الإشعارات"
        className={`relative w-11 h-11 rounded-xl border flex items-center justify-center transition-all ${
          count > 0 && location.pathname === "/"
            ? "bg-sky-50 border-sky-300 text-sky-700"
            : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
        }`}
      >
        <Inbox className="w-5 h-5" />
        {count > 0 && (
          <span
            data-testid="inbox-bell-count"
            className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-sky-600 text-white text-[11px] font-black flex items-center justify-center ring-2 ring-white"
          >
            {count}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="inbox-dropdown"
          className="absolute left-0 top-full mt-2 w-96 max-w-[95vw] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden z-50"
        >
          <div className="px-4 py-3 bg-gradient-to-l from-sky-50 to-white border-b border-slate-100 flex items-center justify-between">
            <div className="font-black text-slate-900 flex items-center gap-2">
              <Bell className="w-4 h-4 text-sky-600" /> الإشعارات
            </div>
            <span className="text-xs text-slate-500">{count} جديد</span>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-8 text-center text-slate-400 text-sm">لا توجد إشعارات بعد</div>
            ) : (
              items.map((n) => {
                const meta = KIND_META[n.kind] || { icon: Bell, label: n.kind };
                const Icon = meta.icon;
                return (
                  <button
                    key={n.id}
                    onClick={() => goTo(n)}
                    data-testid={`inbox-item-${n.id}`}
                    className={`w-full text-right px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors flex items-start gap-3 ${!n.read ? "bg-sky-50/30" : ""}`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${!n.read ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-500"}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-[11px] text-slate-500">
                        <span className="font-bold">{meta.label}</span>
                        {n.child_name && <><span>·</span><span>{n.child_name}</span></>}
                      </div>
                      <div className="text-sm text-slate-800 font-semibold mt-0.5 line-clamp-2">{n.preview || n.from_name}</div>
                      <div className="text-[10px] text-slate-400 mt-1">{new Date(n.timestamp).toLocaleString("ar-EG")}</div>
                    </div>
                    {!n.read && <span className="w-2 h-2 rounded-full bg-sky-600 mt-2 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
